import { useState, type FormEvent } from "react";
import { Button, DataTable, Input, type DataTableColumn } from "@shop-ledger/ui-components";
import { useAuth } from "../../auth/AuthContext";
import { createWebhookSubscription, deleteWebhookSubscription, listWebhookDeliveries, listWebhookSubscriptions } from "../../api/webhooksApi";
import type { WebhookDeliveryOut, WebhookSubscriptionCreateResponse, WebhookSubscriptionOut } from "../../api/types";
import { StatusBanner } from "../../components/StatusBanner";
import { ApiError } from "../../lib/apiClient";
import { useAsyncData } from "../../lib/useAsyncData";

const SUB_COLUMNS_BASE: DataTableColumn<WebhookSubscriptionOut, keyof WebhookSubscriptionOut>[] = [
  { key: "target_url", header: "Target URL", render: (r) => r.target_url },
  { key: "event_types", header: "Events", render: (r) => r.event_types.join(", ") || "—" },
  { key: "is_active", header: "Status", render: (r) => <span className={`badge ${r.is_active ? "badge--current" : "badge--overdue"}`}>{r.is_active ? "Active" : "Inactive"}</span> },
  { key: "created_at", header: "Created", render: (r) => new Date(r.created_at).toLocaleString() },
];

const DELIVERY_COLUMNS: DataTableColumn<WebhookDeliveryOut, keyof WebhookDeliveryOut>[] = [
  { key: "event_type", header: "Event", render: (r) => r.event_type },
  { key: "status", header: "Status", render: (r) => <span className={`badge ${r.status === "DELIVERED" ? "badge--current" : r.status === "FAILED" ? "badge--overdue" : "badge--due-soon"}`}>{r.status}</span> },
  { key: "attempt_count", header: "Attempts", align: "right", render: (r) => r.attempt_count },
  { key: "last_attempted_at", header: "Last attempt", render: (r) => (r.last_attempted_at ? new Date(r.last_attempted_at).toLocaleString() : "—") },
  { key: "created_at", header: "Created", render: (r) => new Date(r.created_at).toLocaleString() },
];

const KNOWN_EVENT_TYPES = ["sale.created"];

/**
 * Webhooks — subscription CRUD + delivery history (§12.6), OWNER-only. HMAC-SHA256 signed,
 * bounded-retry delivery server-side (`app/services/webhook_service.py`); the raw secret
 * is shown exactly once, at creation, and never re-displayed by `GET /webhooks/subscriptions`.
 */
export function WebhooksPage() {
  const { user, ensureFreshToken } = useAuth();
  const isOwner = user?.role === "OWNER";
  const subsState = useAsyncData(() => ensureFreshToken().then((token) => listWebhookSubscriptions(token)), []);
  const deliveriesState = useAsyncData(() => ensureFreshToken().then((token) => listWebhookDeliveries(token)), []);

  const [targetUrl, setTargetUrl] = useState("");
  const [eventTypes, setEventTypes] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [created, setCreated] = useState<WebhookSubscriptionCreateResponse | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<WebhookSubscriptionOut | null>(null);

  function toggleEventType(eventType: string) {
    setEventTypes((prev) => (prev.includes(eventType) ? prev.filter((e) => e !== eventType) : [...prev, eventType]));
  }

  async function handleCreate(event: FormEvent) {
    event.preventDefault();
    setFormError(null);
    setCreated(null);
    setSubmitting(true);
    try {
      const token = await ensureFreshToken();
      const subscription = await createWebhookSubscription(token, { target_url: targetUrl, event_types: eventTypes });
      setCreated(subscription);
      setTargetUrl("");
      setEventTypes([]);
      subsState.reload();
    } catch (error) {
      setFormError(error instanceof ApiError ? error.message : "Could not create webhook subscription.");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleConfirmDelete() {
    if (!pendingDelete) return;
    setDeletingId(pendingDelete.id);
    try {
      const token = await ensureFreshToken();
      await deleteWebhookSubscription(token, pendingDelete.id);
      setPendingDelete(null);
      subsState.reload();
    } catch (error) {
      setFormError(error instanceof ApiError ? error.message : "Could not delete subscription.");
    } finally {
      setDeletingId(null);
    }
  }

  if (!isOwner) {
    return (
      <div className="page">
        <div className="page__header">
          <div>
            <h1 className="page__title">Webhooks</h1>
          </div>
        </div>
        <StatusBanner kind="info">Webhook management is OWNER-only. Your role ({user?.role}) doesn't have access.</StatusBanner>
      </div>
    );
  }

  const subColumns: DataTableColumn<WebhookSubscriptionOut, keyof WebhookSubscriptionOut>[] = [
    ...SUB_COLUMNS_BASE,
    {
      key: "id",
      header: "",
      render: (r) => (
        <Button variant="danger" size="md" type="button" disabled={deletingId === r.id} onClick={() => setPendingDelete(r)}>
          Delete
        </Button>
      ),
    },
  ];

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Webhooks</h1>
          <p className="page__subtitle">Outbound event subscriptions + delivery history, OWNER-only.</p>
        </div>
      </div>

      <div className="panel">
        <h2 className="panel__title">New subscription</h2>
        <form onSubmit={handleCreate}>
          <div className="form-grid">
            <Input label="Target URL" type="url" value={targetUrl} onChange={(e) => setTargetUrl(e.target.value)} placeholder="https://example.com/webhooks/shop-ledger" required />
          </div>
          <div className="sl-field" style={{ marginBottom: 16 }}>
            <span className="sl-field__label">Event types</span>
            <div style={{ display: "flex", gap: 12 }}>
              {KNOWN_EVENT_TYPES.map((eventType) => (
                <label key={eventType} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <input type="checkbox" checked={eventTypes.includes(eventType)} onChange={() => toggleEventType(eventType)} />
                  <code>{eventType}</code>
                </label>
              ))}
            </div>
          </div>
          {formError ? <StatusBanner kind="error">{formError}</StatusBanner> : null}
          <div className="form-actions">
            <Button type="submit" disabled={submitting}>
              {submitting ? "Creating…" : "Create subscription"}
            </Button>
          </div>
        </form>

        {created ? (
          <div className="banner banner--warning" style={{ marginTop: 16 }}>
            <p style={{ marginTop: 0 }}>
              <strong>{created.note}</strong>
            </p>
            <code style={{ display: "block", wordBreak: "break-all", background: "#0f172a", color: "#e2e8f0", padding: "8px 10px", borderRadius: 6 }}>{created.secret}</code>
          </div>
        ) : null}
      </div>

      {pendingDelete ? (
        <div className="panel">
          <h2 className="panel__title">Delete subscription to {pendingDelete.target_url}?</h2>
          <p className="muted">This stops delivery immediately — any in-flight retries for past events are abandoned. This cannot be undone.</p>
          <div className="form-actions">
            <Button variant="danger" type="button" disabled={deletingId === pendingDelete.id} onClick={() => void handleConfirmDelete()}>
              {deletingId === pendingDelete.id ? "Deleting…" : "Delete subscription"}
            </Button>
            <Button variant="ghost" type="button" onClick={() => setPendingDelete(null)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : null}

      <div className="panel">
        <h2 className="panel__title">Subscriptions</h2>
        {subsState.error ? <StatusBanner kind="error">{subsState.error}</StatusBanner> : null}
        {subsState.loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <DataTable columns={subColumns} rows={subsState.data ?? []} getRowId={(row) => row.id} emptyMessage="No webhook subscriptions yet." caption="Webhook Subscriptions" />
        )}
      </div>

      <div className="panel">
        <h2 className="panel__title">Delivery history</h2>
        {deliveriesState.error ? <StatusBanner kind="error">{deliveriesState.error}</StatusBanner> : null}
        {deliveriesState.loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <DataTable columns={DELIVERY_COLUMNS} rows={deliveriesState.data ?? []} getRowId={(row) => row.id} emptyMessage="No deliveries yet." caption="Webhook Deliveries" />
        )}
      </div>
    </div>
  );
}
